
import { useState, useEffect, useRef } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { ChevronRight, ArrowLeft, CheckCircle, Calendar } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { Textarea } from "@/components/ui/textarea";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import TestimonialsPanel from "@/components/premium/TestimonialsPanel";

// Create schema for interview booking
const formSchema = z.object({
  interviewType: z.string({ required_error: "Please select an interview type" }),
  company: z.string({ required_error: "Please select a company" }),
  otherCompany: z.string().optional(),
  level: z.string().optional(),
  notes: z.string().optional(),
  name: z.string().min(1, { message: "Please enter your name" }),
  email: z.string().email({ message: "Please enter a valid email address" }),
});

export default function MockInterview() {
  const [step, setStep] = useState(1);
  const [selectedLevel, setSelectedLevel] = useState<string | null>(null);
  const [selectedInterviewType, setSelectedInterviewType] = useState<string | null>(null);
  const [calendlyLoaded, setCalendlyLoaded] = useState(false);
  const [calendlyEventId, setCalendlyEventId] = useState<string | null>(null);
  const [scheduledAt, setScheduledAt] = useState<string | null>(null);
  const [bookingComplete, setBookingComplete] = useState(false);
  const calendlyRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      interviewType: "",
      company: "",
      level: "",
      otherCompany: "",
      notes: "",
      email: user?.email || "",
      name: "",
    },
    mode: "onChange"
  });

  // Initialize Calendly widget when step 2 is reached
  useEffect(() => {
    if (step === 2 && calendlyRef.current) {
      // Clear previous instances if any
      if (calendlyRef.current.innerHTML !== '') {
        calendlyRef.current.innerHTML = '';
      }

      // Use your specific Calendly URL
      const calendlyUrl = "https://calendly.com/reachpriyaa/mock-ml-interview";
      
      if (window.Calendly) {
        window.Calendly.initInlineWidget({
          url: calendlyUrl,
          parentElement: calendlyRef.current,
          prefill: {
            name: form.getValues('name'),
            email: form.getValues('email')
          },
          utm: {}
        });

        // Add event listener for Calendly events
        window.addEventListener('message', async function(e) {
          if (e.data.event && e.data.event.indexOf('calendly') === 0) {
            // Calendly event fired
            if (e.data.event === 'calendly.event_scheduled') {
              // Event was scheduled
              const eventDetails = e.data.payload;
              console.log('Calendly event scheduled:', eventDetails);
              
              // Store the event ID and scheduled time
              setCalendlyEventId(eventDetails.event.uri);
              setScheduledAt(eventDetails.event.start_time);

              // Automatically move to payment processing
              handlePaymentProcess();
            }
          }
        });
      }
      
      setCalendlyLoaded(true);
    }
  }, [step, form]);

  // Function to handle step validation
  const handleStepSubmit = async (stepNumber: number) => {
    if (stepNumber === 1) {
      // Validate step 1 fields (only company now)
      const step1Valid = await form.trigger(['company']);
      if (step1Valid) {
        // Set name and email from user if available
        if (user) {
          form.setValue('email', user.email || '');
          // You could also set name if available in user profile
        }
        setStep(2);
      }
    }
  };

  // Function to handle payment and store session
  const handlePaymentProcess = async () => {
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please sign in to schedule a mock interview.",
        variant: "destructive",
      });
      navigate("/login");
      return;
    }

    try {
      const formData = form.getValues();
      
      // Save the session details to the database
      const company = formData.company === "Other" ? formData.otherCompany : formData.company;
      
      const { error } = await supabase
        .from('candidate_sessions')
        .insert({
          user_id: user.id,
          session_type: formData.interviewType,
          company,
          level: formData.level,
          notes: formData.notes,
          calendly_event_id: calendlyEventId,
          scheduled_at: scheduledAt
        });

      if (error) {
        throw error;
      }
      
      // Proceed to payment - use coaching payment for one-time payment
      try {
        const interviewTypeData = interviewTypes.find(t => t.id === formData.interviewType);
        const amount = interviewTypeData?.price || 225;
        
        const { data, error } = await supabase.functions.invoke('create-coaching-payment', {
          body: { 
            amount,
            description: `Mock Interview - ${interviewTypeData?.title}`
          },
        });

        if (error) {
          console.error('Error from create-coaching-payment function:', error);
          throw new Error(error.message);
        }
        
        if (!data || !data.url) {
          console.error('Invalid response from create-coaching-payment:', data);
          throw new Error('Invalid checkout response');
        }
        
        console.log('Checkout session created, redirecting to:', data.url);
        // Redirect to Stripe Checkout
        window.location.href = data.url;
      } catch (error: any) {
        console.error('Error creating checkout session:', error);
        toast({
          title: "Payment Processing Error",
          description: `There was a problem setting up the payment. Please try again.`,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error saving session:", error);
      toast({
        title: "Scheduling Error",
        description: "There was a problem saving your session. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Handle successful payment return
  useEffect(() => {
    // Check URL for success parameter
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('success') === 'true') {
      // Clear the URL parameter
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // Show success toast and set booking complete state
      toast({
        title: "Interview Scheduled!",
        description: "Your ML Engineering interview has been scheduled. You'll receive a confirmation email shortly.",
      });
      
      setBookingComplete(true);
    }
  }, []);

  // Function to manually advance to next step
  const nextStep = () => {
    handleStepSubmit(step);
  };

  const goBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleLevelSelect = (level: string) => {
    setSelectedLevel(level);
    form.setValue("level", level);
  };

  const handleInterviewTypeSelect = (type: string) => {
    setSelectedInterviewType(type);
    form.setValue("interviewType", type);
  };

  // Interview types
  const interviewTypes = [
    { 
      id: "ml_coding", 
      title: "ML Coding Interview", 
      description: "Mock ML coding and modeling interview",
      price: 225
    },
    { 
      id: "ml_system_design", 
      title: "ML System Design Interview", 
      description: "End-to-end ML system design practice",
      price: 225
    },
    { 
      id: "coding", 
      title: "Coding Interview", 
      description: "DSA and coding interview practice",
      price: 199
    }
  ];

  // Mock companies
  const companies = [
    "Google", 
    "Meta", 
    "Amazon", 
    "Apple", 
    "Microsoft", 
    "Netflix", 
    "Other"
  ];

  // Level options specific to ML Engineers
  const levelOptions = [
    { 
      id: "junior", 
      title: "Junior ML Engineer", 
      subtitle: "L3, 0-2 YOE" 
    },
    { 
      id: "mid", 
      title: "ML Engineer", 
      subtitle: "L4, 3-4 YOE" 
    },
    { 
      id: "senior", 
      title: "Senior ML Engineer", 
      subtitle: "L5, 5+ YOE" 
    },
    { 
      id: "staff", 
      title: "Staff ML Engineer", 
      subtitle: "L6" 
    },
    { 
      id: "senior-staff", 
      title: "Senior Staff ML Engineer", 
      subtitle: "L7" 
    },
    { 
      id: "principal", 
      title: "Principal ML Engineer", 
      subtitle: "L8+" 
    }
  ];

  // If booking is complete, show confirmation screen
  if (bookingComplete) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        
        <main className="flex-1">
          <section className="bg-gradient-to-b from-primary/5 to-background pt-16 pb-12 md:pt-24 md:pb-20">
            <div className="container">
              <h1 className="text-4xl md:text-5xl font-bold mb-6 text-center">
                Interview Scheduled!
              </h1>
              <p className="text-xl text-muted-foreground text-center max-w-3xl mx-auto">
                We'll send you a confirmation email with all the details
              </p>
            </div>
          </section>
          
          <section className="py-16">
            <div className="container max-w-3xl">
              <Card className="p-8 shadow-sm text-center">
                <div className="mb-8 flex flex-col items-center justify-center">
                  <div className="rounded-full bg-green-100 p-3 mb-4">
                    <CheckCircle className="h-8 w-8 text-green-600" />
                  </div>
                  <h2 className="text-2xl font-bold mb-2">Your Interview is Confirmed</h2>
                  <p className="text-muted-foreground">Check your email for the calendar invitation</p>
                </div>
                
                <div className="flex flex-col md:flex-row justify-center gap-6 mb-8">
                  <div className="p-4 bg-blue-50 rounded-lg flex items-center gap-3">
                    <Calendar className="h-5 w-5 text-blue-600" />
                    <div className="text-left">
                      <p className="text-sm text-blue-600 font-medium">Interview Date</p>
                      <p className="text-blue-800">
                        {scheduledAt ? new Date(scheduledAt).toLocaleDateString(undefined, {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        }) : "Date will be in your email"}
                      </p>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-blue-50 rounded-lg flex items-center gap-3">
                    <Calendar className="h-5 w-5 text-blue-600" />
                    <div className="text-left">
                      <p className="text-sm text-blue-600 font-medium">Interview Time</p>
                      <p className="text-blue-800">
                        {scheduledAt ? new Date(scheduledAt).toLocaleTimeString(undefined, {
                          hour: '2-digit',
                          minute: '2-digit',
                        }) : "Time will be in your email"}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h3 className="font-medium text-lg">What to expect next:</h3>
                  <ul className="text-left space-y-2 max-w-md mx-auto">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>You'll receive a confirmation email with meeting details</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>Prepare for your interview using our resources on the Learn page</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>Our ML expert will meet you at the scheduled time</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>After the session, you'll receive detailed feedback and next steps</span>
                    </li>
                  </ul>
                </div>
                
                <div className="mt-8 pt-6 border-t">
                  <Button onClick={() => navigate('/learn')} className="min-w-[200px]">
                    Explore Learning Resources
                  </Button>
                </div>
              </Card>
            </div>
          </section>
        </main>
        
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-1">
        <section className="bg-gradient-to-b from-primary/5 to-background pt-16 pb-12 md:pt-24 md:pb-20">
          <div className="container">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-center">
              Schedule a Mock Interview
            </h1>
            <p className="text-xl text-muted-foreground text-center max-w-3xl mx-auto">
              Choose from ML System Design, ML Coding, or DSA interviews tailored to your target company
            </p>
          </div>
        </section>
        
        <section className="py-16">
          <div className="container max-w-7xl">
            <div className="grid lg:grid-cols-12 gap-8">
              {/* Left sidebar - Testimonials */}
              <div className="lg:col-span-3">
                <TestimonialsPanel />
              </div>
              
              {/* Main content - Interview booking */}
              <div className="lg:col-span-9">
                <div className="mb-10">
                  <Tabs 
                    defaultValue={step.toString()} 
                    value={step.toString()}
                    className="w-full"
                  >
                    <TabsList className="grid grid-cols-2 w-full mb-8">
                      <TabsTrigger value="1" disabled>
                        Interview Details
                      </TabsTrigger>
                      <TabsTrigger value="2" disabled>
                        Schedule
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                </div>
                
                <Card className="p-6 shadow-sm">
                  <Form {...form}>
                    <form onSubmit={form.handleSubmit(handlePaymentProcess)} className="space-y-6">
                      {step === 1 && (
                        <>
                          <div className="space-y-6">
                            <div>
                              <h2 className="text-2xl font-semibold mb-6">Which company are you interviewing for?</h2>
                              
                              <FormField
                                control={form.control}
                                name="company"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormControl>
                                      <Select
                                        onValueChange={(value) => {
                                          field.onChange(value);
                                        }}
                                        defaultValue={field.value}
                                      >
                                        <SelectTrigger className="text-lg h-12">
                                          <SelectValue placeholder="Select a company" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {companies.map((company) => (
                                            <SelectItem key={company} value={company}>{company}</SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              
                              {form.watch("company") === "Other" && (
                                <FormField
                                  control={form.control}
                                  name="otherCompany"
                                  render={({ field }) => (
                                    <FormItem className="mt-4">
                                      <FormControl>
                                        <Input {...field} placeholder="Enter company name" className="text-lg h-12" />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                              )}
                            </div>
                          </div>
                          
                          <div className="flex justify-end pt-4">
                            <Button type="button" onClick={nextStep}>
                              Next
                              <ChevronRight className="ml-2 h-4 w-4" />
                            </Button>
                          </div>
                        </>
                      )}
                      
                      {step === 2 && (
                        <>
                          <div className="space-y-4">
                            <h3 className="text-lg font-medium">Schedule Your Interview</h3>
                            <p className="text-muted-foreground">Choose a convenient time for your mock interview</p>
                            
                            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                              {/* Left column: Additional options and notes */}
                              <div className="lg:col-span-4 space-y-6">
                                <div className="bg-blue-50 rounded-md p-4">
                                  <h4 className="font-medium text-blue-800 mb-2">About Your Interview</h4>
                                  <ul className="text-sm text-blue-700 space-y-2">
                                    <li>• 60-minute realistic mock interview</li>
                                    <li>• {interviewTypes.find(t => t.id === form.watch("interviewType"))?.title}</li>
                                    <li>• Tailored to {form.watch("company") === "Other" ? form.watch("otherCompany") : form.watch("company")}</li>
                                    <li>• Detailed feedback provided</li>
                                  </ul>
                                </div>
                                
                                <FormField
                                  control={form.control}
                                  name="notes"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Additional notes (optional)</FormLabel>
                                      <FormControl>
                                        <Textarea 
                                          placeholder="Share any specific questions or topics you want to focus on"
                                          className="min-h-[150px]"
                                          {...field}
                                        />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />

                                <div className="rounded-md bg-blue-50 p-4 mt-6">
                                  <div className="flex">
                                    <div className="ml-3">
                                      <h3 className="text-sm font-medium text-blue-800">
                                        Interview Summary
                                      </h3>
                                      <div className="mt-2 text-sm text-blue-700">
                                        <p className="mb-1">Type: {interviewTypes.find(t => t.id === form.watch("interviewType"))?.title}</p>
                                        <p className="mb-1">Company: {form.watch("company") === "Other" ? form.watch("otherCompany") : form.watch("company")}</p>
                                        <p className="mb-1">Level: {form.watch("level") ? levelOptions.find(l => l.id === form.watch("level"))?.title : "Not selected"}</p>
                                        <p className="mt-2 font-semibold">Price: ${interviewTypes.find(t => t.id === form.watch("interviewType"))?.price}</p>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Right column: Calendly widget */}
                              <div className="lg:col-span-8 border rounded-md">
                                <div 
                                  ref={calendlyRef}
                                  className="calendly-inline-widget" 
                                  style={{ minWidth: '320px', height: '630px' }}
                                ></div>
                                
                                {!calendlyLoaded && (
                                  <div className="flex justify-center items-center h-[630px]">
                                    <p>Loading calendar...</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex justify-between pt-4">
                            <Button 
                              type="button" 
                              variant="outline" 
                              onClick={goBack}
                              className="flex items-center gap-2"
                            >
                              <ArrowLeft className="h-4 w-4" />
                              Back
                            </Button>
                          </div>
                        </>
                      )}
                    </form>
                  </Form>
                </Card>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
}
